import { ActionTree, Commit } from 'vuex';
import RootState from '@/store/root_state';
import { ContentProgress, ContinueStudying, UndergradProgressState } from './state';
import SentryClient from '@/helpers/sentry';
import { eponaProgressApi } from '@/helpers/eponaProgressApi';

interface LessonProgress extends ContentProgress {
  discursiveExerciseProgresses: ContentProgress[];
  practiceExerciseProgresses: ContentProgress[];
  objectiveExerciseProgresses: ContentProgress[];
  textProgresses: ContentProgress[];
  videoProgresses: ContentProgress[];
}

interface DisciplineProgress extends ContentProgress {
  lessonProgresses: LessonProgress[];
}

interface ModuleProgress extends ContentProgress {
  disciplineProgresses: DisciplineProgress[];
}

const getContentId = (content: ContentProgress) =>
  content.discursiveExerciseId ||
  content.practiceExerciseId ||
  content.objectiveExerciseId ||
  content.textId ||
  content.videoId;

const getProgress = (content: ContentProgress) => Math.round(content.percentage * 100);

const KEY_CONTENTS_STORAGE = 'contents-to-try-again-to-register-progress';

const getStorageContents = (): ContentProgress[] =>
  JSON.parse(localStorage.getItem(KEY_CONTENTS_STORAGE) || '[]');

const addContentToStorage = (content: ContentProgress): void =>
  localStorage.setItem(KEY_CONTENTS_STORAGE, JSON.stringify([...getStorageContents(), content]));

let isProcessingContentRegistration = false;
const contentsToRegister: ContentProgress[] = [];

const processRegistrationOfContents = async (commit: Commit, newContent: ContentProgress) => {
  contentsToRegister.push(newContent);
  commit('SET_CONTENT_PROGRESS', {
    id: getContentId(newContent),
    progress: 100,
    content: newContent,
  });

  if (isProcessingContentRegistration) return;
  isProcessingContentRegistration = true;

  while (contentsToRegister.length) {
    const currentContent = contentsToRegister.shift()!;

    // eslint-disable-next-line no-await-in-loop
    await eponaProgressApi(`register/${currentContent.type}/consumption`, currentContent).catch(
      (error) => {
        addContentToStorage(currentContent);
        SentryClient.captureError(error);
      },
    );
    commit('SET_PROGRESS_OUTDATED', true);
  }
  isProcessingContentRegistration = false;
};

const waitProcessingContentRegistration = (resolve: (value?: any) => void) => {
  if (isProcessingContentRegistration)
    setTimeout(waitProcessingContentRegistration.bind(this, resolve), 100);
  else resolve();
};

const waitForAllProgressRegisters = async (commit: Commit) => {
  commit('SET_PROGRESS_LOADING', true);

  getStorageContents().map((content) => processRegistrationOfContents(commit, content));
  localStorage.removeItem(KEY_CONTENTS_STORAGE);

  await new Promise(waitProcessingContentRegistration);

  commit('SET_PROGRESS_LOADING', false);
};

const getCourseProgress = async (
  courseId: string,
): Promise<[course: any, successfulRequest: boolean]> => {
  const keyCourseStorage = `last-progress-in-course-${courseId}`;

  try {
    const {
      data: { data: course },
    } = await eponaProgressApi(`course/${courseId}`);

    localStorage.setItem(keyCourseStorage, JSON.stringify(course));

    return [course, true];
  } catch (error) {
    const course = JSON.parse(localStorage.getItem(keyCourseStorage) || '0');

    if (course) SentryClient.captureError(error);
    else throw error;

    return [course, false];
  }
};

const actions: ActionTree<UndergradProgressState, RootState> = {
  PULL_PROGRESS: async ({ commit, getters }, courseId: string) => {
    await waitForAllProgressRegisters(commit);

    if (!getters.isProgressOutdated) return;

    try {
      const lessonsInProgress: ContinueStudying[] = [];
      const lessonsWithFullProgress: ContinueStudying[] = [];

      commit('SET_PROGRESS_LOADING', true);

      const [course, successfulRequest] = await getCourseProgress(courseId);

      commit('SET_COURSE_PROGRESS', { id: course.courseId, progress: getProgress(course) });

      course.subModuleProgresses.forEach((module: ModuleProgress) => {
        commit('SET_MODULE_PROGRESS', { id: module.subModuleId, progress: getProgress(module) });

        module.disciplineProgresses.forEach((discipline: DisciplineProgress) => {
          commit('SET_DISCIPLINE_PROGRESS', {
            progress: getProgress(discipline),
            content: { ...module, ...discipline },
          });

          discipline.lessonProgresses.forEach((lesson: LessonProgress) => {
            const progress = getProgress(lesson);
            commit('SET_LESSON_PROGRESS', {
              progress,
              content: { ...module, ...discipline, ...lesson },
            });

            if (progress > 0) {
              (progress === 100 ? lessonsWithFullProgress : lessonsInProgress).push({
                disciplineInstanceId: discipline.disciplineVersionId as string,
                lessonId: lesson.lessonId,
              });
            }

            [
              ...lesson.discursiveExerciseProgresses,
              ...lesson.practiceExerciseProgresses,
              ...lesson.objectiveExerciseProgresses,
              ...lesson.textProgresses,
              ...lesson.videoProgresses,
            ].forEach((content: ContentProgress) => {
              commit('SET_CONTENT_PROGRESS', {
                id: getContentId(content),
                progress: getProgress(content),
                content: { ...module, ...discipline, ...lesson, ...content },
              });
            });
          });
        });
      });

      commit(
        'SET_LESSONS_TO_CONTINUE_STUDYING',
        (lessonsInProgress.length ? lessonsInProgress : lessonsWithFullProgress).slice(-4),
      );
      if (successfulRequest) {
        commit('SET_PROGRESS_OUTDATED', false);
        commit('SET_PROGRESS_LOADING', false);
      }
    } catch (e) {
      SentryClient.captureError(e);
    }
  },

  REGISTER_PROGRESS: ({ commit }, content: ContentProgress) => {
    processRegistrationOfContents(commit, {
      ...content,
      moment: new Date().toISOString(),
    });
  },
};

export default actions;
